import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Toolbar,
  Typography,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../hooks/store-hooks";
import { Transition } from "../../../../../../components";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useListaCboService } from "../../../../../../../use-cases/geo-veles/lista-cbo";
import { useEffect, useState } from "react";
import {
  setOpenDialogListaCboState,
  selectListaCbo,
} from "../../../../../../../application/states/geo-veles/lista-cbo";
import { theme } from "../../../../../../styles";
import { PreviaFiltroListaCbo } from "./forms/previaFiltroListaCboIntern";
import { useUIService } from "../../../../../../../use-cases/ui";
import { IListaCbo } from "../../../../../../../application/models/geo-veles/IListaCbo";
import { addListaCBOFilterState } from "../../../../../../../application/states/geo-veles";

export const DialogListaCbo = () => {
  const open = useAppSelector((state) => state.listaCbo.openDialogListaCbo);
  const listasCbo = useAppSelector((state) => state.listaCbo.listasCbo);

  const dispatch = useAppDispatch();
  const { setAlert } = useUIService();

  const {
    openCreateListaCboForm,
    openEditListaCboForm,
    deleteListaCbo,
  } = useListaCboService();

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
  const [listaToDelete, setListaToDelete] = useState<IListaCbo | null>(null);

  useEffect(() => {
    if (open) {
      setSearchTerm("");
    }
  }, [open]);

  const handleClose = () => {
    dispatch(setOpenDialogListaCboState(false));
  };

  const handleSelectLista = (lista: IListaCbo) => {
    dispatch(selectListaCbo({ lista }));
    dispatch(addListaCBOFilterState({ idVelesGeoListaCBO: lista.IdVelesGeoListaCBO }));

  };

  const handleEdit = (lista: IListaCbo) => {
    openEditListaCboForm(lista);
  };

  const handleDeleteClick = (lista: IListaCbo) => {
    setListaToDelete(lista);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!listaToDelete) return;

    try {
      await deleteListaCbo(listaToDelete.IdVelesGeoListaCBO);
      setAlert({
        message: `Lista "${listaToDelete.Nome}" excluída com sucesso!`,
        type: "success"
      });
    } catch (error) {
    } finally {
      setConfirmDeleteOpen(false);
      setListaToDelete(null);
    }
  };

  const filteredListas = listasCbo.filter((lista) =>
    lista.Nome.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedListasCbo = useAppSelector((state) => state.listaCbo.selectedListasCbo);

  return (
    <>
      <Dialog
        fullScreen
        TransitionComponent={Transition}
        open={open}
        onClose={handleClose}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <Typography
              sx={{ ml: 2, flex: 1 }}
              variant="h6"
              component="div"
              textTransform="uppercase"
            >
              Minhas Listas de CBO
            </Typography>
            <Button autoFocus color="inherit" onClick={handleClose}>
              Fechar
            </Button>
          </Toolbar>
        </AppBar>

        <Box p={3}>
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Buscar lista de CBO"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Box pl={3}>
            <form onSubmit={(e) => e.preventDefault()}>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    onClick={(e) => {
                      e.preventDefault();
                      openCreateListaCboForm(true);
                    }}
                    startIcon={<AddIcon />}
                  >
                    Criar nova lista de CBOs
                  </Button>
                </Grid>
                <Grid item xs={2}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      const todasListas = listasCbo ?? [];
                      todasListas.forEach((lista) => {
                        dispatch(selectListaCbo({ lista }));
                        dispatch(addListaCBOFilterState({ idVelesGeoListaCBO: lista.IdVelesGeoListaCBO }));
                      });
                    }}
                  >
                    Incluir todas as listas
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>

          <Grid container spacing={3}>
            {/* Lista de listas disponíveis (à esquerda) */}
            <Grid item xs={12} md={6}>
              {filteredListas.length > 0 ? (
                <List>
                  {filteredListas.map((lista) => (
                    <Box key={lista.IdVelesGeoListaCBO}>
                      <ListItem
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          px: 2,
                          py: 1.5,
                          cursor: "pointer",
                        }}
                        onClick={() => handleSelectLista(lista)}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <ListItemText
                            primary={lista.Nome}
                            secondary={`${Array.isArray(lista.CBOs) ? lista.CBOs.length : 0} ocupações`}
                            primaryTypographyProps={{ noWrap: true }}
                            secondaryTypographyProps={{ noWrap: true }}
                          />
                        </Box>

                        {/* Ações: Editar / Excluir */}
                        <Box display="flex" gap={1} ml={1}>
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(lista);
                            }}
                            color="primary"
                          >
                            Editar
                          </Button>
                          <Button
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(lista);
                            }}
                            color="error"
                          >
                            Excluir
                          </Button>
                        </Box>
                      </ListItem>
                      <Divider sx={{ borderColor: theme.palette.primary.main }} />
                    </Box>
                  ))}
                </List>
              ) : (
                <Box textAlign="center" py={4}>
                  <Typography color="textSecondary" variant="body1">
                    {searchTerm
                      ? "Nenhuma lista encontrada com esse nome."
                      : "Nenhuma lista de ocupações salva ainda."}
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* Painel de listas selecionadas (à direita) */}
            {selectedListasCbo.length > 0 && (
              <Grid item xs={12} md={6} >
                <Box px={3}>
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography
                        variant="h5"
                        color="primary"
                        fontWeight="bold"
                        textTransform="uppercase"
                      >
                        Listas selecionadas
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <PreviaFiltroListaCbo />
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => {
          setConfirmDeleteOpen(false);
          setListaToDelete(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir a lista{" "}
            <strong>{listaToDelete?.Nome}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmDeleteOpen(false);
              setListaToDelete(null);
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            startIcon={<DeleteIcon />}
            variant="contained"
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};